/**
 * Édition d'un exercice : réglages de progression, questions, aperçu élève.
 *
 * `validation_reasons` vient du **serveur** et n'est pas recalculé ici. C'est la
 * même règle que `can_delete` en phase 3 : refaire INV-C4/C5 côté écran donnerait
 * deux implémentations à maintenir, et celle du navigateur se tromperait en
 * silence le jour où la règle bouge. L'éditeur affiche ce que le serveur dit,
 * question par question.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import {
  useAddQuestion,
  useDeleteExercise,
  useDeleteQuestion,
  useExercisePreview,
  useReorderQuestions,
  useUpdateExercise,
  useUpdateQuestion,
  type Exercise,
} from "@/features/catalog/api/useCatalog";
import { ExercisePreview } from "@/features/catalog/components/ExercisePreview";
import { QuestionForm } from "@/features/catalog/components/QuestionForm";
import {
  draftOf,
  emptyDraft,
  hasAnswerKeys,
  hasChoices,
  type QuestionDraft,
} from "@/features/catalog/lib/questions";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { asDocument, EMPTY_DOCUMENT, toStored } from "@/shared/richtext/document";
import { RichTextEditor } from "@/shared/richtext/RichTextEditor";
import { RichContent } from "@/shared/richtext/RichContent";
import { Card } from "@/shared/ui/Card";

const FIELD_CLASSES =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm " +
  "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";

interface ExerciseEditorProps {
  courseId: string;
  lessonId: string;
  exercise: Exercise;
}

export function ExerciseEditor({ courseId, lessonId, exercise }: ExerciseEditorProps) {
  const { t } = useTranslation("catalog");
  const errorMessage = useApiErrorMessage();

  const updateExercise = useUpdateExercise(courseId);
  const deleteExercise = useDeleteExercise(courseId);
  const addQuestion = useAddQuestion(courseId);
  const deleteQuestion = useDeleteQuestion(courseId);
  const reorderQuestions = useReorderQuestions(courseId);

  const updateQuestion = useUpdateQuestion(courseId);

  const [draft, setDraft] = useState<QuestionDraft>(() => emptyDraft());
  const [editing, setEditing] = useState<{ id: string; draft: QuestionDraft } | undefined>(
    undefined,
  );
  const [showPreview, setShowPreview] = useState(false);
  const preview = useExercisePreview(courseId, showPreview ? exercise.id : undefined);

  function settings(patch: Parameters<typeof updateExercise.mutate>[0]["settings"]) {
    updateExercise.mutate({ lessonId, exerciseId: exercise.id, settings: patch });
  }

  function move(index: number, delta: number) {
    const ids = exercise.questions.map((question) => question.id);
    const moved = ids[index];
    const displaced = ids[index + delta];
    // Les deux bornes sont vérifiées ici plutôt qu'affirmées par un `!` : le
    // bouton est déjà désactivé aux extrémités, mais une garde qui dit la
    // vérité vaut mieux qu'une promesse faite au compilateur.
    if (moved === undefined || displaced === undefined) {
      return;
    }
    const questionIds = ids.map((id, position) => {
      if (position === index) {
        return displaced;
      }
      return position === index + delta ? moved : id;
    });
    reorderQuestions.mutate({ lessonId, exerciseId: exercise.id, questionIds });
  }

  return (
    <Card title={exercise.title}>
      {exercise.validation_reasons.length > 0 && (
        <Alert tone="warning" className="mb-4">
          <ul className="list-inside list-disc">
            {exercise.validation_reasons.map((reason) => (
              <li key={reason}>{t(`exercises.reasons.${reason}`)}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Les consignes n'avaient **aucun** écran avant le 06/09/2026 : la
          colonne existait depuis la phase 4, la route les acceptait, et rien ne
          les proposait. Un champ qu'aucun formulaire ne montre est un champ que
          personne ne remplit. */}
      <div className="mb-4">
        <RichTextEditor
          label={t("exercises.instructions")}
          documentKey={exercise.id}
          content={asDocument(exercise.instructions)}
          onBlur={(instructions) => {
            // Enregistré à la perte du focus, comme le seuil de réussite juste
            // en dessous : brancher la mutation sur chaque frappe enverrait une
            // requête par caractère. La comparaison évite la requête vide que
            // le simple fait d'ouvrir l'écran produirait.
            if (JSON.stringify(instructions) !== JSON.stringify(exercise.instructions ?? null)) {
              settings({ instructions });
            }
          }}
          hint={t("exercises.instructionsHint")}
          minHeight="min-h-20"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={exercise.is_blocking}
            onChange={(event) => {
              settings({ is_blocking: event.target.checked });
            }}
          />
          {t("exercises.isBlocking")}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={exercise.shuffle}
            onChange={(event) => {
              settings({ shuffle: event.target.checked });
            }}
          />
          {t("exercises.shuffle")}
        </label>
        <div>
          <label
            className="mb-1 block text-sm font-medium"
            htmlFor={`passing-score-${exercise.id}`}
          >
            {t("exercises.passingScore")}
          </label>
          <input
            id={`passing-score-${exercise.id}`}
            className={FIELD_CLASSES}
            type="number"
            min={1}
            max={100}
            defaultValue={exercise.passing_score}
            onBlur={(event) => {
              settings({ passing_score: event.target.value });
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor={`max-attempts-${exercise.id}`}>
            {t("exercises.maxAttempts")}
          </label>
          <input
            id={`max-attempts-${exercise.id}`}
            className={FIELD_CLASSES}
            type="number"
            min={1}
            defaultValue={exercise.max_attempts ?? ""}
            placeholder={t("exercises.unlimited")}
            onBlur={(event) => {
              // Le champ vidé vaut `null` — « sans limite » —, et non zéro, qui
              // serait un exercice que personne ne peut faire.
              settings({
                max_attempts: event.target.value === "" ? null : Number(event.target.value),
              });
            }}
          />
        </div>
      </div>

      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        {t("exercises.gradingMode")} : {t(`exercises.gradingModes.${exercise.grading_mode}`)} ·{" "}
        {t("exercises.maxScore", { total: exercise.max_score })}
      </p>

      <h4 className="mt-5 mb-2 text-sm font-semibold">{t("exercises.questions")}</h4>
      {exercise.questions.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{t("exercises.noQuestion")}</p>
      ) : (
        <ol className="space-y-3">
          {exercise.questions.map((question, index) => (
            <li
              key={question.id}
              className="rounded-md border border-slate-200 p-3 dark:border-slate-700"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex gap-1 text-sm font-medium">
                    <span>{question.position}.</span>
                    <RichContent document={asDocument(question.prompt)} />
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t(`exercises.types.${question.type}`)} ·{" "}
                    {t("exercises.points", { points: question.points })}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    aria-label={t("exercises.editQuestion")}
                    onClick={() => {
                      // Le brouillon part de la question **telle que le serveur
                      // la rend**, `id` des propositions compris : c'est lui qui
                      // fait que réenregistrer conserve leurs lignes, et donc
                      // les copies déjà rendues.
                      setEditing(
                        editing?.id === question.id
                          ? undefined
                          : { id: question.id, draft: draftOf(question) },
                      );
                    }}
                  >
                    ✏
                  </Button>
                  <Button
                    variant="ghost"
                    aria-label={t("exercises.moveUp")}
                    disabled={index === 0}
                    onClick={() => {
                      move(index, -1);
                    }}
                  >
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    aria-label={t("exercises.moveDown")}
                    disabled={index === exercise.questions.length - 1}
                    onClick={() => {
                      move(index, 1);
                    }}
                  >
                    ↓
                  </Button>
                  <Button
                    variant="ghost"
                    aria-label={t("exercises.removeQuestion")}
                    onClick={() => {
                      deleteQuestion.mutate({
                        lessonId,
                        exerciseId: exercise.id,
                        questionId: question.id,
                      });
                    }}
                  >
                    ×
                  </Button>
                </div>
              </div>
              {question.choices.length > 0 && (
                <ul className="mt-2 space-y-1 text-sm">
                  {question.choices.map((choice) => (
                    <li key={choice.id} className="flex items-center gap-2">
                      <span aria-hidden="true">{choice.is_correct ? "✓" : "·"}</span>
                      <span className={choice.is_correct ? "font-medium" : undefined}>
                        {choice.label}
                      </span>
                      {choice.is_correct && (
                        <span className="sr-only">{t("exercises.correctAnswer")}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {question.validation_reasons.length > 0 && (
                <ul className="mt-2 list-inside list-disc text-xs text-amber-700 dark:text-amber-400">
                  {question.validation_reasons.map((reason) => (
                    <li key={reason}>{t(`exercises.reasons.${reason}`)}</li>
                  ))}
                </ul>
              )}
              {editing?.id === question.id && (
                <div className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-700">
                  <QuestionForm
                    draft={editing.draft}
                    // Le type ne se change pas : il est figé à la création côté
                    // serveur, et un menu qui échouerait vaut moins que pas de
                    // menu du tout.
                    allowTypeChange={false}
                    onChange={(draft) => {
                      setEditing({ id: question.id, draft });
                    }}
                    disabled={updateQuestion.isPending}
                    submitLabel={t("editor.save")}
                    onSubmit={() => {
                      updateQuestion.mutate(
                        {
                          lessonId,
                          exerciseId: exercise.id,
                          questionId: question.id,
                          prompt: toStored(editing.draft.prompt) ?? EMPTY_DOCUMENT,
                          explanation: toStored(editing.draft.explanation),
                          choices: hasChoices(editing.draft.type)
                            ? editing.draft.choices.filter((choice) => choice.label.trim() !== "")
                            : undefined,
                          answerKeys: hasAnswerKeys(editing.draft.type)
                            ? editing.draft.answerKeys.filter((key) => key.value.trim() !== "")
                            : undefined,
                        },
                        {
                          onSuccess: () => {
                            setEditing(undefined);
                          },
                        },
                      );
                    }}
                  />
                  {updateQuestion.isError && (
                    <Alert tone="error" className="mt-3">
                      {errorMessage(updateQuestion.error)}
                    </Alert>
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      <div className="mt-5 rounded-md border border-dashed border-slate-300 p-3 dark:border-slate-700">
        <h4 className="mb-3 text-sm font-semibold">{t("exercises.addQuestion")}</h4>
        <QuestionForm
          draft={draft}
          onChange={setDraft}
          disabled={addQuestion.isPending}
          submitLabel={t("exercises.addQuestion")}
          onSubmit={() => {
            addQuestion.mutate(
              {
                lessonId,
                exerciseId: exercise.id,
                type: draft.type,
                // `?? {}` n'arrive jamais : le bouton d'envoi est désactivé
                // sur un énoncé vide. Le serveur refuse d'ailleurs le document
                // vide sur un champ obligatoire — c'est lui la garantie.
                prompt: toStored(draft.prompt) ?? EMPTY_DOCUMENT,
                explanation: toStored(draft.explanation),
                // Envoyées seulement quand le type les attend : le serveur
                // refuse des propositions sur une question libre, et le
                // formulaire ne doit pas produire ce qu'il sait invalide.
                choices: hasChoices(draft.type)
                  ? draft.choices.filter((choice) => choice.label.trim() !== "")
                  : undefined,
                answerKeys: hasAnswerKeys(draft.type)
                  ? draft.answerKeys.filter((key) => key.value.trim() !== "")
                  : undefined,
              },
              {
                onSuccess: () => {
                  setDraft(emptyDraft(draft.type));
                },
              },
            );
          }}
        />
        {addQuestion.isError && (
          <Alert tone="error" className="mt-3">
            {errorMessage(addQuestion.error)}
          </Alert>
        )}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          onClick={() => {
            setShowPreview(!showPreview);
          }}
        >
          {showPreview ? t("exercises.hidePreview") : t("exercises.preview")}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            deleteExercise.mutate({ lessonId, exerciseId: exercise.id });
          }}
        >
          {t("exercises.remove")}
        </Button>
      </div>

      {showPreview && preview.data !== undefined && (
        <div className="mt-4 rounded-md bg-slate-50 p-4 dark:bg-slate-800/50">
          <ExercisePreview preview={preview.data} />
        </div>
      )}

      {updateExercise.isError && (
        <Alert tone="error" className="mt-3">
          {errorMessage(updateExercise.error)}
        </Alert>
      )}
    </Card>
  );
}
